/**
 * Resolve which Cognito app client Amplify should use when E1 may store
 * tokens under legacy, spa-local, or spa-sso-sp client ids.
 *
 * Cookie names look like: CognitoIdentityServiceProvider.<clientId>.…
 * Prefer LastAuthUser cookies as the signal of an active session.
 */

const COGNITO_COOKIE_PREFIX = 'CognitoIdentityServiceProvider.'
const LAST_AUTH_USER_SUFFIX = '.LastAuthUser'

/**
 * Build the allowlist: default first, then any extras (deduped).
 */
export function buildClientIdAllowlist(defaultClientId, additionalClientIds = []) {
  const ids = [defaultClientId, ...additionalClientIds].map((id) => id?.trim()).filter(Boolean)
  return [...new Set(ids)]
}

function getCookieValue(cookieHeader, name) {
  const parts = cookieHeader.split(';')
  for (const part of parts) {
    const trimmed = part.trim()
    if (!trimmed) continue
    const eq = trimmed.indexOf('=')
    const key = eq === -1 ? trimmed : trimmed.slice(0, eq)
    if (key === name) {
      const value = eq === -1 ? '' : trimmed.slice(eq + 1)
      try {
        return decodeURIComponent(value)
      } catch {
        return value
      }
    }
  }
  return undefined
}

/**
 * Client ids that have a Cognito LastAuthUser cookie (active Amplify/Cognito session).
 */
export function findClientIdsWithLastAuthUser(cookieHeader, allowlist) {
  if (!cookieHeader || allowlist.length === 0) {
    return []
  }

  const found = []
  for (const clientId of allowlist) {
    const lastAuthKey = `${COGNITO_COOKIE_PREFIX}${clientId}${LAST_AUTH_USER_SUFFIX}`
    if (getCookieValue(cookieHeader, lastAuthKey) !== undefined) {
      found.push(clientId)
    }
  }
  return found
}

/**
 * Client ids that appear in any CognitoIdentityServiceProvider.<id>.* cookie name.
 * Fallback when LastAuthUser is missing but token cookies exist.
 */
export function findClientIdsFromCookieNames(cookieHeader, allowlist) {
  if (!cookieHeader || allowlist.length === 0) {
    return []
  }

  const allowlistSet = new Set(allowlist)
  const found = new Set()

  for (const part of cookieHeader.split(';')) {
    const trimmed = part.trim()
    if (!trimmed.startsWith(COGNITO_COOKIE_PREFIX)) continue
    const eq = trimmed.indexOf('=')
    const name = eq === -1 ? trimmed : trimmed.slice(0, eq)
    const rest = name.slice(COGNITO_COOKIE_PREFIX.length)
    const dot = rest.indexOf('.')
    const clientId = dot === -1 ? rest : rest.slice(0, dot)
    if (clientId && allowlistSet.has(clientId)) {
      found.add(clientId)
    }
  }

  return allowlist.filter((id) => found.has(id))
}

/**
 * Prefer a non-default match when several allowlisted sessions exist (spa-local / spa-sso-sp
 * over legacy). Among non-defaults, keep allowlist order.
 */
export function pickPreferredClientId(matches, defaultClientId) {
  if (matches.length === 0) {
    return undefined
  }
  const nonDefault = matches.find((id) => id !== defaultClientId)
  return nonDefault ?? matches[0]
}

/**
 * Pick which allowlisted client id Amplify should bind to.
 *
 * Priority:
 * 1. Allowlisted clients with a LastAuthUser cookie (non-default preferred)
 * 2. Allowlisted clients present in any Cognito cookie name (non-default preferred)
 * 3. defaultClientId
 */
export function resolveCognitoClientId({ defaultClientId, allowlist, cookieHeader = '' }) {
  const effectiveAllowlist =
    allowlist.length > 0 ? allowlist : buildClientIdAllowlist(defaultClientId, [])

  const withLastAuth = findClientIdsWithLastAuthUser(cookieHeader, effectiveAllowlist)
  const chosenFromLastAuth = pickPreferredClientId(withLastAuth, defaultClientId)
  if (chosenFromLastAuth) {
    return chosenFromLastAuth
  }

  const fromNames = findClientIdsFromCookieNames(cookieHeader, effectiveAllowlist)
  const chosenFromNames = pickPreferredClientId(fromNames, defaultClientId)
  if (chosenFromNames) {
    return chosenFromNames
  }

  return defaultClientId
}
